import type { SessionResponse, User } from '@promtver/contracts';

import { apiRequest } from './client';
import { ApiError } from './errors';
import { fetchCurrentUser } from './profile';

/**
 * Вход в панель администратора: e-mail/пароль (ТЗ §4.3).
 * Для роли «Администратор региона» 2FA обязательна — сервер возвращает
 * требование подтверждения, клиент запрашивает код отдельным шагом.
 */
export type LoginResult =
  | { kind: 'ok'; session: SessionResponse }
  | { kind: 'two_factor'; challengeId: string };

interface RawSession {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: User;
  session?: RawSession;
}

function normalizeSession(raw: unknown): SessionResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as RawSession;
  const candidate = obj.session ?? obj;
  if (!candidate.accessToken || !candidate.refreshToken) return null;
  return {
    accessToken: candidate.accessToken,
    refreshToken: candidate.refreshToken,
    expiresIn: candidate.expiresIn ?? 3600,
    user: (candidate.user ?? {}) as User,
  };
}

function readChallengeId(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as {
    challengeId?: string;
    challenge_id?: string;
    error?: { details?: Record<string, unknown> };
  };
  if (typeof obj.challengeId === 'string') return obj.challengeId;
  if (typeof obj.challenge_id === 'string') return obj.challenge_id;
  const fromDetails = obj.error?.details?.['challengeId'];
  return typeof fromDetails === 'string' ? fromDetails : null;
}

export function normalizeLoginResult(body: unknown): LoginResult {
  const session = normalizeSession(body);
  if (session) {
    return { kind: 'ok', session };
  }

  const obj = (body ?? {}) as {
    requiresTwoFactor?: boolean;
    requires_2fa?: boolean;
    twoFactorRequired?: boolean;
  };
  const requiresTwoFactor = Boolean(
    obj.requiresTwoFactor || obj.requires_2fa || obj.twoFactorRequired,
  );
  const challengeId = readChallengeId(body);
  if (requiresTwoFactor && challengeId) {
    return { kind: 'two_factor', challengeId };
  }

  throw new ApiError('invalid_credentials', '', 401);
}

/**
 * Дополнение сессии профилем пользователя.
 * Сервер отдаёт в /auth/login только токены, поэтому роль добираем из /auth/me.
 * Ошибку запроса профиля не поднимаем: вход состоялся, а роль проверит панель.
 */
async function resolveUser(current: User): Promise<User> {
  if (current.id) return current;
  try {
    const profile = await fetchCurrentUser();
    return profile ?? current;
  } catch {
    return current;
  }
}

export async function loginWithPassword(email: string, password: string): Promise<LoginResult> {
  try {
    const body = await apiRequest<unknown>('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    });
    const result = normalizeLoginResult(body);
    if (result.kind === 'ok') {
      return { kind: 'ok', session: { ...result.session, user: await resolveUser(result.session.user) } };
    }
    return result;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'two_factor_required') {
      const challengeId = readChallengeId({ error: { details: error.details } }) ?? '';
      return { kind: 'two_factor', challengeId };
    }
    throw error;
  }
}

export async function confirmTwoFactor(challengeId: string, code: string): Promise<SessionResponse> {
  const body = await apiRequest<unknown>('/auth/login/2fa', {
    method: 'POST',
    body: { challengeId, code },
    auth: false,
  });
  const session = normalizeSession(body);
  if (!session) {
    throw new ApiError('invalid_two_factor_code', '', 400);
  }
  return { ...session, user: await resolveUser(session.user) };
}
